import {
	ICustomerStateStore,
	CustomerContext,
	CustomerGroup,
	LoyaltyActivity,
	LoyaltyProfile,
	NewsletterCustomization
} from 'coreStore/data/customer/types';
import {ECustomerGender, ECustomerAuthType, ICustomerAddress, ICustomerPaymentInstrument} from './ICustomer';

import CustomerPaymentInstrumentModel from './CustomerPaymentInstrumentModel';
import ManagerRepository from '../ManagerRepository';

/**
 * Represents a customer
 */
class Customer {
	private managerRepository: ManagerRepository;
	private hasOrders: boolean;
	private customerPersonalizationKey?: string;
	private customerGroupsKey?: string;
	private crmId: string;

	public authType: string;
	public customerId: string;
	public customerNo: string;
	public context: CustomerContext;
	public email: string;
	public firstName: string;
	public lastName: string;
	public birthday: string;
	public gender: ECustomerGender;
	public salutation: string;
	public newsletter: boolean;
	public acceptsMarketing: boolean;
	public authorizationDataCollection: boolean;
	public privacyAuthorization: boolean;
	public USConsent: boolean;
	public phoneCode: string;
	public phone: string;
	public city: string;
	public country: string;
	public citizenship: string;
	public customerGroups: Array<CustomerGroup>;
	public loyaltyProfile: LoyaltyProfile;
	public loyaltyActivity: LoyaltyActivity;
	public addresses: Array<ICustomerAddress>;
	public paymentInstruments: Array<ICustomerPaymentInstrument>;
	public storeCredits: object;
	public isJustRegistered: boolean;
	public isLoginOnBehalf: boolean;
	public agent: boolean;
	public isFirstAuthComplete: boolean;
	public siteId: string;
	public needsCommerceContext: boolean;
	public usid: string;
	public needsBasketMerge: boolean;
	public isServer: boolean;
	public newsletterSubscriptions: string[];
	public contactEmail: boolean;
	public contactSms: boolean;
	public contactWhatsapp: boolean;
	public contactPhone: boolean;
	public newsletterCustomization: NewsletterCustomization;

	/**
	 * @constructor
	 * @param customer customer
	 * @param managerRepository managerRepository
	 */
	constructor(customer: ICustomerStateStore, managerRepository: ManagerRepository) {
		this.managerRepository = managerRepository;

		this.customerId = customer.customerId;
		this.customerNo = customer.customerNo;
		this.authType = customer.authType;
		this.isJustRegistered = customer.isJustRegistered;
		this.context = customer.context;
		this.customerGroups = customer.customerGroups ?? [];
		this.email = customer.email;
		this.firstName = customer.firstName;
		this.lastName = customer.lastName;
		this.birthday = customer.birthday;
		this.gender = customer.gender;
		this.salutation = customer.salutation ?? '';
		this.newsletter = customer.newsletter;
		this.newsletterCustomization = customer.newsletterCustomization;
		this.authorizationDataCollection = customer.authorizationDataCollection;
		this.phone = customer.phone;
		this.phoneCode = customer.phoneCode;
		this.city = customer.city;
		this.country = customer.country;
		this.citizenship = customer.citizenship;
		this.loyaltyProfile = customer.loyaltyProfile;
		this.loyaltyActivity = customer.loyaltyActivity;
		this.addresses = [];
		this.paymentInstruments = customer.paymentInstruments;
		this.hasOrders = customer.hasOrders;
		this.customerPersonalizationKey = customer.customerPersonalizationKey;
		this.customerGroupsKey = customer.customerGroupsKey;
		this.storeCredits = customer.storeCredits;
		this.authorizationDataCollection = customer.authorizationDataCollection;
		this.privacyAuthorization = customer.privacyAuthorization;
		this.USConsent = customer.USConsent;
		this.contactEmail = !!customer.contactEmail;
		this.contactSms = !!customer.contactSms;
		this.contactWhatsapp = !!customer.contactWhatsapp;
		this.contactPhone = !!customer.contactPhone;
		this.crmId = customer.crmId;
		this.isLoginOnBehalf = customer.isLoginOnBehalf;
		this.agent = !!customer.agent;
		this.isFirstAuthComplete = !!customer.isFirstAuthComplete;
		this.siteId = customer.siteId;
		this.needsCommerceContext = !!customer.needsCommerceContext;
		this.usid = customer.usid;
		this.needsBasketMerge = customer.needsBasketMerge;
		this.isServer = customer.isServer;
		this.newsletterSubscriptions = customer.newsletterSubscriptions;

		const preferredAddressIndex = customer.addresses
			? customer.addresses.findIndex((address) => address.preferred)
			: -1;

		if (preferredAddressIndex > 0) {
			this.addresses = [
				customer.addresses[preferredAddressIndex],
				...customer.addresses.slice(preferredAddressIndex, preferredAddressIndex + 1),
			];
		} else {
			this.addresses = customer.addresses || [];
		}
	}

	/**
	 * Returns boolean value whether the user data is initialized
	 */
	get isInitialized() {
		return !!this?.customerId
			&& (
				this.isServer
				|| this.siteId === this.managerRepository.getLocalesMgr().getCurrentCountry().getSiteId()
			);
	}

	/**
	 * Returns boolean value whether the customer is of type `registered` or not.
	 */
	get isRegistered() {
		return this?.authType === ECustomerAuthType.REGISTERED;
	}

	/**
	 * Returns boolean value whether the customer is of type `guest` or not.
	 */
	get isGuest() {
		return this?.authType === ECustomerAuthType.GUEST;
	}

	/**
	 * Returns customer email address
	 * @returns {string} - customer email address
	 */
	getEmail(): string | null {
		return this.isRegistered ? this.email : null;
	}

	/**
	 * Returns the customer's saved addresses with the 'preferred' address in the first index
	 * @returns {Array<ICustomerAddress>} an array of customer addresses
	 */
	getAddresses(): ICustomerAddress[] {
		return this.addresses;
	}

	/**
	 * Returns boolean value whether the customer data is fully loaded or not.
	 * @returns {boolean} true if customer is ready.
	 */
	isReady() {
		return this.isInitialized && this.customerGroups?.length > 0;
	}

	/**
	 * Returns boolean value whether the customer is agent
	 * @returns {boolean} true if customer is an agent
	 */
	isAgent(): boolean {
		return this.agent;
	}

	/**
	 * Returns the customer's address with the given address id
	 * @returns {ICustomerAddress|null} customer address
	 */
	getAddress(addressId: string): ICustomerAddress {
		const address = this.addresses.filter((address) => address.addressId === addressId);

		return address.length ? address[0] : null;
	}

	/**
	 * Returns the customer's preferred shipping address (if any)
	 * @returns {CustomerAddress} customer address
	 */
	getPreferredShippingAddress(): ICustomerAddress {
		const addresses: ICustomerAddress[] = this.getAddresses();

		return addresses && addresses.length && addresses[0].preferred ? addresses[0] : null;
	}

	/**
	 * Returns the customer's saved payments
	 * @returns {Array<CustomerPaymentInstrumentModel>} an array of customer payments
	 */
	getPaymentInstruments(): CustomerPaymentInstrumentModel[] {
		return this.paymentInstruments?.map(
			(paymentInstrument) => new CustomerPaymentInstrumentModel(paymentInstrument as any),
		);
	}

	/**
	 * Returns the customer's refinement
	 * @returns {string} customer refinement
	 */
	getCustomerPersonalizationKey(): string {
		return this.customerPersonalizationKey;
	}

	/**
	 * Returns the customer groups
	 * @returns {Array<CustomerGroup>} customer refinement
	 */
	getCustomerGroups(): Array<CustomerGroup> {
		return this.customerGroups;
	}

	/**
	 * Returns customer crmId
	 * @returns {string} customer crmId
	 */
	getCustomerCrmId(): string {
		return this.crmId ? this.crmId : '';
	}

	/**
	 * Checks if the customer is subscribed to newsletter
	 * @returns {boolean} is customer subscribed to newsletter
	 */
	isSubscribedToNewsletter(): boolean {
		return !!this.newsletter;
	}

	/**
	 * Checks if the customer has authorized data collection
	 * @returns {boolean} is data collection authorized
	 */
	isDataCollectionAuthorized(): boolean {
		return !!this.authorizationDataCollection;
	}

	/**
	 * get customer privacy authorization status
	 * @returns {boolean} privacy authorization status
	 */
	getPrivacyAuthorizationStatus(): boolean {
		return this.privacyAuthorization;
	}

	/**
	 * Checks if the customer has given US-specific marketing consent
	 * @returns {boolean} US consent status
	 */
	getUSConsentStatus(): boolean {
		return !!this.USConsent;
	}

	/**
	 * Checks if the customer has orders
	 * @returns {boolean} is customer has orders
	 */
	isHasOrders(): boolean {
		return !!this.hasOrders;
	}

	/**
	 * Returns true if customer is member of that group.
	 * @param {string} customerGroupName customer group name
	 * @returns {boolean} true if customer is member of that group.
	 */
	isMemberOfCustomerGroup(customerGroupName: string): boolean {
		return !!this.customerGroups?.find((customerGroup: CustomerGroup) => {
			return customerGroup.ID === customerGroupName;
		});
	}

	/**
	 * Returns uuid v5 key for customer groups
	 * @returns {string} customer groups key
	 */
	getCustomerGroupsKey(): string {
		return this.customerGroupsKey;
	}

	/**
	 * Checks if the customer can login on behalf
	 * @returns {boolean} true if login on behalf is permitted, false otherwise
	 */
	canLoginOnBehalf(): boolean {
		return !!this.isLoginOnBehalf;
	}
}

export default Customer;
